import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Dashboard.css';
import { SideMenuBar } from '../../SideMenu';
import { NewHeader } from '../../NewHeader';
import { Table, Tag } from 'antd';
import { GoogleMap, LoadScript } from '@react-google-maps/api';
import ReactApexChart from 'react-apexcharts';
import { API_URL } from '../../Config';
import { useAuth } from '../../AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

/* ---------------------------------------------------------
   Icons. Inline SVG replaces the previous <img> tags so the
   icons stay crisp, inherit currentColor, and no longer
   depend on a relative asset path that breaks on nested
   routes.
   --------------------------------------------------------- */

const Icon = ({ d, size = 16, fill = 'none', ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    {d}
  </svg>
);

const IconSearch = (p) => (
  <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" /></>} />
);
const IconClose = (p) => <Icon {...p} d={<path d="M18 6 6 18M6 6l12 12" />} />;
const IconRefresh = (p) => (
  <Icon {...p} d={<><path d="M21 12a9 9 0 1 1-2.6-6.4" /><path d="M21 3v6h-6" /></>} />
);
const IconUsers = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M16 20v-1.6a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" />
        <circle cx="9" cy="7.5" r="3.5" />
        <path d="M22 20v-1.6a4 4 0 0 0-3-3.8" />
        <path d="M16.5 4.2a4 4 0 0 1 0 6.6" />
      </>
    }
  />
);
const IconLayers = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="m12 2.8 9 4.7-9 4.7-9-4.7 9-4.7Z" />
        <path d="m3 12.5 9 4.7 9-4.7" />
        <path d="m3 17.2 9 4.7 9-4.7" />
      </>
    }
  />
);
const IconCheck = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="9" /><path d="m8.4 12.2 2.5 2.5 4.7-4.9" /></>} />
);
const IconAlert = (p) => (
  <Icon {...p} d={<><circle cx="12" cy="12" r="9" /><path d="M12 7.6v5" /><path d="M12 16.2h.01" /></>} />
);
const IconArrow = (p) => <Icon {...p} d={<><path d="M4 12h15" /><path d="m13 6 6 6-6 6" /></>} />;
const IconPencil = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M4 20h4.2L19.4 8.8a2.1 2.1 0 0 0 0-3l-1.2-1.2a2.1 2.1 0 0 0-3 0L4 15.8V20Z" />
        <path d="m14.4 6.4 3.2 3.2" />
      </>
    }
  />
);
const IconTrash = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M4 6.5h16" />
        <path d="M9 6.5V5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5v1.5" />
        <path d="M6.5 6.5 7.4 19a1.6 1.6 0 0 0 1.6 1.5h6a1.6 1.6 0 0 0 1.6-1.5l.9-12.5" />
      </>
    }
  />
);
const IconInbox = (p) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M3 13h5l1.6 2.6h4.8L16 13h5" />
        <path d="M5.4 5h13.2l2.4 8v5a1.6 1.6 0 0 1-1.6 1.6H4.6A1.6 1.6 0 0 1 3 18v-5l2.4-8Z" />
      </>
    }
  />
);

/* Initials for avatar chips. Display only. */
const initialsOf = (value = '') =>
  String(value)
    .replace(/\(.*\)/, '')
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

const percentOf = (part, whole) =>
  !whole ? 0 : Math.round((Number(part) / Number(whole)) * 100);

/* Layout-only hook. Lets the task queue switch between a
   table and a stacked card list at narrow widths. It reads
   the viewport and nothing else. */
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mql = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);

    setMatches(mql.matches);

    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }

    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
};

const priorityClass = (tag) =>
  tag === 'High' ? 'tag_high' : tag === 'Medium' ? 'tag_medium' : 'tag_low';

const statusClass = (tag) =>
  tag === 'Completed'
    ? 'tag_completed'
    : tag === 'Pending'
      ? 'tag_pending'
      : 'tag_progress';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user.user;

  const [tasks, setTasks] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [inCompleteTask, setInCompleteTask] = useState([]);
  const [completedTask, setCompletedTask] = useState([]);
  const [inCompleteTaskCount, setInCompleteTaskCount] = useState(0);
  const [completedTaskCount, setCompletedTaskCount] = useState(0);

  const [currentLocation, setCurrentLocation] = useState({ lat: 0, lng: 0 });
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [taskSearch, setTaskSearch] = useState('');
  const [activeModal, setActiveModal] = useState(null);

  /* UI-only state. None of this touches the data layer. */
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [viewFilter, setViewFilter] = useState('all');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [locationState, setLocationState] = useState('locating'); // locating | ready | denied

  const searchRef = useRef(null);
  const confirmTimer = useRef(null);

  /* Below 760px the queue renders as cards. Between 760 and
     1100 the Role column is dropped so the rest fits without
     sideways scrolling. */
  const isCompact = useMediaQuery('(max-width: 760px)');
  const isMedium = useMediaQuery('(max-width: 1100px)');

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocationState('ready');
      },
      (error) => {
        console.error('Error getting current location:', error);
        setLocationState('denied');
      }
    );
  }, []);

  const center = useMemo(
    () => ({
      lat: currentLocation.lat,
      lng: currentLocation.lng,
    }),
    [currentLocation]
  );

  useEffect(() => {
    let endpoint = API_URL + `/users/?filter=${username.username}`;

    if (username.role === 'Admin' || username.role === 'HR') {
      endpoint = API_URL + '/users';
    }

    fetch(endpoint)
      .then((response) => response.json())
      .then((data) => {
        const activeUsers = data.filter(
          (item) =>
            item.punch_in !== null &&
            item.punch_in !== undefined &&
            (item.punch_out === null ||
              item.punch_out === undefined ||
              item.punch_out === '')
        );
        setActiveEmployees(activeUsers);
      })
      .catch((error) => console.error(error));
  }, [username.username, username.role]);

  useEffect(() => {
    setActiveUserCount(activeEmployees.length);
  }, [activeEmployees]);

  useEffect(() => {
    let endpoint = API_URL + `/tasks/?assignee=${username.username}`;

    if (username.role === 'Admin' || username.role === 'HR') {
      endpoint = API_URL + '/tasks/all';
    }

    fetch(endpoint)
      .then((response) => response.json())
      .then((data) => {
        const completed = data.filter((task) => task.status === 'Completed');
        const incomplete = data.filter((task) => task.status === 'Pending');

        setTasks(data);
        setCompletedTask(completed);
        setInCompleteTask(incomplete);
      })
      .catch((error) => console.error(error))
      .finally(() => setIsLoading(false));
  }, [username.username, username.role]);

  const fetchTasks = (filterValue) => {
    let endpoint;

    if (filterValue && (username.role === 'Admin' || username.role === 'HR')) {
      endpoint = `${API_URL}/tasks/?assignee=&filter=${filterValue}`;
    } else if (filterValue) {
      endpoint = `${API_URL}/tasks/?assignee=${username.username}&filter=${filterValue}`;
    } else if (username.role === 'Admin' || username.role === 'HR') {
      endpoint = `${API_URL}/tasks/all`;
    } else {
      endpoint = `${API_URL}/tasks/?assignee=${username.username}`;
    }

    setIsRefreshing(true);

    fetch(endpoint)
      .then((response) => response.json())
      .then((data) => {
        const completed = data.filter((task) => task.status === 'Completed');
        const incomplete = data.filter((task) => task.status === 'Pending');

        setTasks(data);
        setCompletedTask(completed);
        setInCompleteTask(incomplete);
      })
      .catch((error) => console.error(error))
      .finally(() => setIsRefreshing(false));
  };

  useEffect(() => {
    setTotalTasks(tasks.length);

    const pending = tasks.filter((item) => item.status === 'Pending').length;
    const completed = tasks.filter((item) => item.status === 'Completed').length;

    setInCompleteTaskCount(pending);
    setCompletedTaskCount(completed);
  }, [tasks]);

  const deleteTask = async (uuId) => {
    try {
      const response = await axios.delete(API_URL + `/tasks/${uuId}`);

      if (response.status === 204) {
        fetchTasks();
      } else {
        console.error('Error deleting task');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getTasksDetailsByName = (uuId) => {
    const filteredTasks = tasks.filter((task) => task.uuId === uuId);
    navigate('/ListViewInfo', { state: { taskDetails: filteredTasks[0] } });
  };

  /* Two-step delete. First click arms the button, second click
     runs the exact same deleteTask call as before. Disarms
     itself after four seconds. */
  const handleDeleteClick = (uuId) => {
    if (pendingDelete === uuId) {
      clearTimeout(confirmTimer.current);
      setPendingDelete(null);
      deleteTask(uuId);
      return;
    }

    setPendingDelete(uuId);
    clearTimeout(confirmTimer.current);
    confirmTimer.current = setTimeout(() => setPendingDelete(null), 4000);
  };

  useEffect(() => () => clearTimeout(confirmTimer.current), []);

  /* Cmd/Ctrl + K focuses search. Escape closes the detail panel. */
  const closeModal = useCallback(() => setActiveModal(null), []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeModal]);

  useEffect(() => {
    if (!activeModal) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [activeModal]);

  const state = {
    series: [
      {
        name: 'Tasks',
        data: [10, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 90],
        color: '#173452',
      },
    ],
    options: {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'Inter, sans-serif',
        parentHeightOffset: 0,
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 600,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 7,
          borderRadiusApplication: 'end',
          columnWidth: '46%',
          horizontal: false,
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          type: 'vertical',
          shadeIntensity: 0,
          gradientToColors: ['#3b5a7c'],
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 0.82,
          stops: [0, 100],
        },
      },
      states: {
        hover: { filter: { type: 'darken', value: 0.88 } },
        active: { filter: { type: 'none' } },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: 'rgba(23,52,82,0.07)',
        strokeDashArray: 4,
        padding: { left: 4, right: 4, top: 0 },
        xaxis: { lines: { show: false } },
      },
      xaxis: {
        categories: [
          'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
        ],
        labels: {
          style: {
            colors: '#77899e',
            fontSize: '10px',
            fontFamily: 'JetBrains Mono, monospace',
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: { show: false },
      },
      yaxis: {
        labels: {
          style: {
            colors: '#9aa9ba',
            fontSize: '10px',
            fontFamily: 'JetBrains Mono, monospace',
          },
        },
      },
      tooltip: {
        theme: 'light',
        style: { fontSize: '11px', fontFamily: 'Inter, sans-serif' },
        marker: { show: false },
      },
    },
  };

  const containerStyle = {
    width: '100%',
    height: '100%',
    borderRadius: '18px',
  };

  const renderTags = (values, resolveClass) => {
    const clean = (values || []).filter(Boolean);

    if (!clean.length) {
      return <span className="cell_empty">Not set</span>;
    }

    return (
      <div className="tag_group">
        {clean.map((tag) => (
          <Tag className={`modern_tag ${resolveClass(tag)}`} key={tag}>
            {tag}
          </Tag>
        ))}
      </div>
    );
  };

  const renderRowActions = (record) => (
    <div className="row_actions">
      <button
        type="button"
        className="table_action"
        onClick={() => getTasksDetailsByName(record.uuId)}
        aria-label={`Open task ${record.Task}`}
        title="Open task"
      >
        <IconPencil size={15} />
      </button>
      <button
        type="button"
        className={`table_action danger ${
          pendingDelete === record.uuId ? 'is_confirming' : ''
        }`}
        onClick={() => handleDeleteClick(record.uuId)}
        aria-label={
          pendingDelete === record.uuId
            ? `Confirm delete task ${record.Task}`
            : `Delete task ${record.Task}`
        }
        title={pendingDelete === record.uuId ? 'Click again to delete' : 'Delete task'}
      >
        {pendingDelete === record.uuId ? 'Confirm' : <IconTrash size={15} />}
      </button>
    </div>
  );

  const columns = [
    {
      title: '#',
      dataIndex: 'Task',
      key: 'Task',
      width: 62,
      render: (value) => (
        <span className="task_index">{String(value).padStart(2, '0')}</span>
      ),
    },
    {
      title: 'Assignee',
      dataIndex: 'Name',
      key: 'Name',
      render: (value) => (
        <span className="task_name">
          <span className="avatar_chip">{initialsOf(value || 'NA')}</span>
          <span className="task_name_text">{value || 'Unassigned'}</span>
        </span>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'Priority',
      key: 'Priority',
      width: 140,
      render: (_, { Priority }) => renderTags(Priority, priorityClass),
    },
    {
      title: 'Status',
      key: 'Status',
      dataIndex: 'Status',
      width: 150,
      render: (_, { Status }) => renderTags(Status, statusClass),
    },
    {
      title: 'Role',
      key: 'Role',
      dataIndex: 'Role',
      width: 140,
      render: (_, { Role }) => renderTags(Role, () => 'tag_role'),
    },
    {
      title: 'Deadline',
      dataIndex: 'Deadline',
      key: 'Deadline',
      width: 170,
      render: (value) =>
        value ? (
          <span className="deadline_value">{value}</span>
        ) : (
          <span className="cell_empty">No deadline</span>
        ),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 120,
      render: (_, record) => renderRowActions(record),
    },
  ];

  /* Drop the Role column on mid-size screens so the remaining
     six fit without a horizontal scrollbar. Presentation only. */
  const responsiveColumns = isMedium
    ? columns.filter((column) => column.key !== 'Role')
    : columns;

  const dataa = tasks.map((item, index) => ({
    key: index.toString(),
    uuId: item.uuId,
    Task: index + 1,
    Name: item.assignee || '',
    Priority: [item.priority || ''],
    Status: [item.status || ''],
    Role: [item.role || ''],
    Deadline: item.deadline || '',
  }));

  /* View filter is presentation only. It narrows the rows shown
     in the main table and changes nothing about what is fetched
     or counted. */
  const visibleRows =
    viewFilter === 'all'
      ? dataa
      : dataa.filter((row) =>
          viewFilter === 'completed'
            ? row.Status.includes('Completed')
            : row.Status.includes('Pending')
        );

  const ColumnsOf_TotalActiveEmployee = [
    { title: '#', dataIndex: 'Srno', key: 'Srno', width: 70 },
    { title: 'Employee', dataIndex: 'Name2', key: 'Name2' },
  ];

  const dataOf_TotalActiveEmployee = activeEmployees.map((item, index) => ({
    key: index.toString(),
    Srno: index + 1,
    Name2: `${item.username} (${item.role})`,
  }));

  const ColumnsOf_TotalTask = [
    { title: '#', dataIndex: 'Srno2', key: 'Srno2', width: 70 },
    { title: 'Employee', dataIndex: 'Name3', key: 'Name3' },
    { title: 'Task Details', dataIndex: 'taskDetails', key: 'taskDetails' },
  ];

  const dataOf_TotalTask = tasks.map((item, index) => ({
    key: index.toString(),
    Srno2: index + 1,
    Name3: item.assignee || '',
    taskDetails: item.taskDetails || '',
  }));

  const ColumnsOf_TotalTaskCompleted = [
    { title: '#', dataIndex: 'Srno3', key: 'Srno3', width: 70 },
    { title: 'Employee', dataIndex: 'Name4', key: 'Name4' },
    { title: 'Task Details', dataIndex: 'taskDetails', key: 'taskDetails' },
  ];

  const dataOf_TotalTaskCompleted = completedTask.map((item, index) => ({
    key: index.toString(),
    Srno3: index + 1,
    Name4: item.assignee || '',
    taskDetails: item.taskDetails || '',
  }));

  const ColumnsOf_TotalPendingTask = [
    { title: '#', dataIndex: 'Srno4', key: 'Srno4', width: 70 },
    { title: 'Employee', dataIndex: 'Name5', key: 'Name5' },
    { title: 'Task Details', dataIndex: 'taskDetails', key: 'taskDetails' },
  ];

  const dataOf_TotalPendingTask = inCompleteTask.map((item, index) => ({
    key: index.toString(),
    Srno4: index + 1,
    Name5: item.assignee || '',
    taskDetails: item.taskDetails || '',
  }));

  const statCards = [
    {
      key: 'active',
      label: 'On shift now',
      value: activeUserCount,
      icon: <IconUsers size={17} />,
      helper: 'Punched in, not yet out',
      className: 'stat_active',
      share: null,
      shareLabel: 'Live',
    },
    {
      key: 'total',
      label: 'Total tasks',
      value: totalTasks,
      icon: <IconLayers size={17} />,
      helper: 'Everything in this workspace',
      className: 'stat_total',
      share: 100,
      shareLabel: 'All work',
    },
    {
      key: 'completed',
      label: 'Completed',
      value: completedTaskCount,
      icon: <IconCheck size={17} />,
      helper: 'Closed and delivered',
      className: 'stat_completed',
      share: percentOf(completedTaskCount, totalTasks),
      shareLabel: 'of total',
    },
    {
      key: 'pending',
      label: 'Pending',
      value: inCompleteTaskCount,
      icon: <IconAlert size={17} />,
      helper: 'Still open, needs a next step',
      className: 'stat_pending',
      share: percentOf(inCompleteTaskCount, totalTasks),
      shareLabel: 'of total',
    },
  ];

  const modalConfig = {
    active: {
      title: 'On shift now',
      subtitle: 'Everyone currently punched in',
      columns: ColumnsOf_TotalActiveEmployee,
      data: dataOf_TotalActiveEmployee,
    },
    total: {
      title: 'All tasks',
      subtitle: 'Every task in this workspace',
      columns: ColumnsOf_TotalTask,
      data: dataOf_TotalTask,
    },
    completed: {
      title: 'Completed tasks',
      subtitle: 'Tasks marked complete',
      columns: ColumnsOf_TotalTaskCompleted,
      data: dataOf_TotalTaskCompleted,
    },
    pending: {
      title: 'Pending tasks',
      subtitle: 'Tasks still waiting on someone',
      columns: ColumnsOf_TotalPendingTask,
      data: dataOf_TotalPendingTask,
    },
  };

  const openModal = (key) => setActiveModal(key);

  const viewTabs = [
    { key: 'all', label: 'All', count: totalTasks },
    { key: 'pending', label: 'Pending', count: inCompleteTaskCount },
    { key: 'completed', label: 'Completed', count: completedTaskCount },
  ];

  const completedShare = percentOf(completedTaskCount, totalTasks);
  const pendingShare = percentOf(inCompleteTaskCount, totalTasks);

  return (
    <div className="dashboard_app">
      <SideMenuBar />

      <main className="content">
        <NewHeader />

        <div className="dashboard_container">
          <section className="dashboard_topbar">
            <div>
              <span className="dashboard_kicker">Workspace overview</span>
              <h1 className="dashboard_Dashboard">Dashboard</h1>
              <p className="dashboard_subtitle">
                Who is on shift, what work is open, and where the team is right now.
              </p>
            </div>

            <div className="dashboard_identity">
              <span className="identity_avatar">
                {initialsOf(username.username)}
                <span className="online_dot" />
              </span>
              <span className="identity_meta">
                <strong>{username.username}</strong>
                <small>{username.role || 'User'}</small>
              </span>
            </div>
          </section>

          <section className="dashboard_toolbar">
            <div className="searchBox">
              <span className="search_icon">
                <IconSearch size={14} />
              </span>
              <input
                ref={searchRef}
                className="search_input1"
                placeholder="Search tasks by name, assignee or status"
                value={taskSearch}
                onChange={(e) => {
                  const text = e.target.value;
                  setTaskSearch(text);
                  fetchTasks(text);
                }}
              />
              {taskSearch ? (
                <button
                  type="button"
                  className="clear_search"
                  onClick={() => {
                    setTaskSearch('');
                    fetchTasks('');
                  }}
                  aria-label="Clear search"
                >
                  <IconClose size={12} />
                </button>
              ) : (
                <kbd>⌘ K</kbd>
              )}
            </div>

            <div className="view_switch" role="tablist" aria-label="Filter tasks by status">
              {viewTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={viewFilter === tab.key}
                  className={`view_switch_btn ${viewFilter === tab.key ? 'is_active' : ''}`}
                  onClick={() => setViewFilter(tab.key)}
                >
                  {tab.label}
                  <span className="view_switch_count">{tab.count}</span>
                </button>
              ))}
            </div>

            <select
              className="sort_by_Dashboard"
              defaultValue="none"
              aria-label="Sort dashboard"
            >
              <option value="none" disabled hidden>Sort by</option>
              <option value="tasks">Total Tasks</option>
              <option value="distance">Total Distance</option>
              <option value="present">Total Days Present</option>
              <option value="absent">Total Days Absent</option>
              <option value="hours">Total Hours Worked</option>
            </select>

            <button
              type="button"
              className={`refresh_button ${isRefreshing ? 'is_spinning' : ''}`}
              onClick={() => fetchTasks(taskSearch)}
              title="Reload tasks"
            >
              <IconRefresh size={14} />
              <span>Refresh</span>
            </button>
          </section>

          <section className="stat_grid">
            {statCards.map((card) => (
              <button
                key={card.key}
                type="button"
                className={`stat_card ${card.className}`}
                onClick={() => openModal(card.key)}
              >
                <div className="stat_card_top">
                  <span className="stat_icon">{card.icon}</span>
                  <span className="stat_more">
                    View details <IconArrow size={12} />
                  </span>
                </div>

                <div className="stat_value">{card.value ?? 0}</div>
                <div className="stat_label">{card.label}</div>
                <div className="stat_helper">{card.helper}</div>

                <div className="stat_meter">
                  <span className="stat_meter_track">
                    <span
                      className="stat_meter_fill"
                      style={{ width: `${card.share === null ? 100 : card.share}%` }}
                    />
                  </span>
                  <span className="stat_meter_value">
                    {card.share === null ? card.shareLabel : `${card.share}% ${card.shareLabel}`}
                  </span>
                </div>
              </button>
            ))}
          </section>

          <section className="analytics_grid">
            <article className="dashboard_panel map_panel">
              <div className="panel_header">
                <div>
                  <span className="panel_eyebrow">Field view</span>
                  <h2>Team activity map</h2>
                </div>
                <span className="live_badge"><i /> Live</span>
              </div>

              <div className="map_container">
                <LoadScript
                  googleMapsApiKey={
                    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
                    'AIzaSyCbqg16HNi8_S2QFwBrXSW39G--VyfLj9s'
                  }
                >
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={center}
                    zoom={12}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: false,
                      clickableIcons: false,
                    }}
                  >
                    <div className="custom-marker" />
                  </GoogleMap>
                </LoadScript>

                {locationState !== 'ready' && (
                  <div className="map_fallback">
                    {locationState === 'locating' ? (
                      <>
                        <span className="map_scan" />
                        <strong>Finding your location</strong>
                        <span>The map centres as soon as your position comes through.</span>
                      </>
                    ) : (
                      <>
                        <strong>Location is turned off</strong>
                        <span>
                          Allow location access in your browser settings, then reload to
                          centre the map on your area.
                        </span>
                      </>
                    )}
                  </div>
                )}

                <div className="map_overlay_card">
                  <span className="map_pulse" />
                  <div>
                    <strong>{activeUserCount} on shift</strong>
                    <small>team members punched in</small>
                  </div>
                  {activeEmployees.length > 0 && (
                    <div className="avatar_stack">
                      {activeEmployees.slice(0, 3).map((item, index) => (
                        <span className="avatar_chip" key={`${item.username}-${index}`}>
                          {initialsOf(item.username)}
                        </span>
                      ))}
                      {activeEmployees.length > 3 && (
                        <span className="avatar_chip is_overflow">
                          +{activeEmployees.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </article>

            <article className="dashboard_panel chart_panel">
              <div className="panel_header">
                <div>
                  <span className="panel_eyebrow">Throughput</span>
                  <h2>Monthly task activity</h2>
                </div>
                <span className="panel_metric">Last 12 months</span>
              </div>
              <div className="chart_container">
                <ReactApexChart
                  options={state.options}
                  series={state.series}
                  type="bar"
                  height="100%"
                />
              </div>
            </article>
          </section>

          <section className="dashboard_panel tasks_panel">
            <div className="tasks_header">
              <div>
                <span className="panel_eyebrow">Work queue</span>
                <h2>Tasks</h2>
                <p>
                  Showing {visibleRows.length} of {tasks.length}{' '}
                  {tasks.length === 1 ? 'task' : 'tasks'}
                  {viewFilter !== 'all' ? ` in ${viewFilter}` : ''}.
                </p>
              </div>
              <div className="tasks_summary">
                <span>
                  <i className="summary_dot completed" /> <b>{completedTaskCount}</b> completed
                </span>
                <span>
                  <i className="summary_dot pending" /> <b>{inCompleteTaskCount}</b> pending
                </span>
              </div>
            </div>

            {totalTasks > 0 && (
              <div
                className="workload_bar"
                role="img"
                aria-label={`${completedShare} percent complete, ${pendingShare} percent pending`}
              >
                <span className="workload_seg completed" style={{ width: `${completedShare}%` }} />
                <span className="workload_seg pending" style={{ width: `${pendingShare}%` }} />
              </div>
            )}

            <div className="table_shell">
              {isLoading ? (
                <div className="skeleton_table">
                  {[0, 1, 2, 3, 4, 5].map((row) => (
                    <div className="skeleton_row" key={row} />
                  ))}
                </div>
              ) : visibleRows.length > 0 ? (
                isCompact ? (
                  <div className="task_cards">
                    {visibleRows.map((row) => (
                      <article className="task_card" key={row.key}>
                        <div className="task_card_head">
                          <span className="task_name">
                            <span className="avatar_chip">
                              {initialsOf(row.Name || 'NA')}
                            </span>
                            <span className="task_name_text">
                              {row.Name || 'Unassigned'}
                            </span>
                          </span>
                          <span className="task_index">
                            {String(row.Task).padStart(2, '0')}
                          </span>
                        </div>

                        <div className="task_card_meta">
                          {renderTags(row.Status, statusClass)}
                          {renderTags(row.Priority, priorityClass)}
                          {renderTags(row.Role, () => 'tag_role')}
                        </div>

                        <div className="task_card_foot">
                          {row.Deadline ? (
                            <span className="deadline_value">Due {row.Deadline}</span>
                          ) : (
                            <span className="cell_empty">No deadline</span>
                          )}
                          {renderRowActions(row)}
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <Table
                    className="modern_dashboard_table"
                    columns={responsiveColumns}
                    dataSource={visibleRows}
                    scroll={{ x: isMedium ? 680 : 860, y: 380 }}
                    pagination={false}
                  />
                )
              ) : (
                <div className="empty_state">
                  <div className="empty_icon">
                    <IconInbox size={22} />
                  </div>
                  <h3>{tasks.length ? 'Nothing in this view' : 'No tasks yet'}</h3>
                  <p>
                    {tasks.length
                      ? 'Switch back to All to see the rest of the queue, or clear your search.'
                      : 'Create a task from the task module and it will show up here.'}
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      {activeModal && (
        <>
          <div className="overlay_Dashboard" onClick={closeModal} />
          <section
            className="popup-form_Dashboard"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dashboard-modal-title"
          >
            <div className="modal_header">
              <div>
                <span className="panel_eyebrow">Detail view</span>
                <h2 id="dashboard-modal-title">{modalConfig[activeModal].title}</h2>
                <p>{modalConfig[activeModal].subtitle}</p>
              </div>
              <button
                type="button"
                className="close_Dashboard"
                onClick={closeModal}
                aria-label="Close detail view"
              >
                <IconClose size={16} />
              </button>
            </div>

            <div className="modal_body">
              <Table
                columns={modalConfig[activeModal].columns}
                dataSource={modalConfig[activeModal].data}
                scroll={{ x: 560, y: 430 }}
                pagination={false}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Dashboard;